import { inject, Injectable } from '@angular/core';

import { Customer } from '../../../../model/customer.model';
import { isAddressSet } from '../../../../services/mandatory-address';
import { RetailCrmNewAccountInput } from '../../model/requests/accounts.model';
import { RetailCrmCustomerFieldsRequestModelMapper } from './customer-fields-model.mapper';

@Injectable({
  providedIn: 'root',
})
export class RetailCrmNewCustomerRequestBodyMapper {
  private readonly customerFieldModelMapper = inject(RetailCrmCustomerFieldsRequestModelMapper);

  /**
   * Map a new customer to the data format accepted by the CRM endpoint
   */
  mapNewAccount(customer: Customer): RetailCrmNewAccountInput {
    const salutation = customer.salutation;
    const firstName = customer.firstName;
    const lastName = customer.lastName;
    const personEmail = customer.privateContact.email;

    if (!salutation || !firstName || !lastName || !personEmail) {
      throw new Error(
        `#CDFTF0043 Failed mapping Customer to RetailCrmNewAccountInput, as required fields are undefined.\n` +
          `Required fields:\n` +
          `  salutation: ${salutation}\n` +
          `  firstName: ${firstName}\n` +
          `  lastName: ${lastName}\n` +
          `  personEmail: ${personEmail}\n`,
      );
    }

    // A business address carrying nothing but a country (and province) counts as not entered
    // (see mandatory-address.ts). It is omitted, so Retail CRM does not store an address without
    // city and postal code - a stored country cannot be removed again via the API.
    const businessAddress = isAddressSet(customer.businessContact.address)
      ? customer.businessContact.address
      : undefined;

    return {
      businessAddressCity: businessAddress?.city,
      businessAddressCountry: businessAddress?.country,
      businessAddressComplement: businessAddress?.addressLine1,
      businessAddressLine2: businessAddress?.addressLine2,
      businessAddressPostBox: businessAddress?.postBox,
      businessAddressPostalCode: businessAddress?.postalCode,
      businessAddressStreet: businessAddress?.street,
      businessAddressStreetNumber: businessAddress?.houseNumber,
      businessAddressState: businessAddress?.province,
      emailBusiness: customer.businessContact.email,
      phoneBusiness: customer.businessContact.phone,
      mobilePhoneBusiness: customer.businessContact.mobile,
      firstName: firstName,
      middleName: customer.middleName,
      lastName: lastName,
      suffix: customer.suffix,
      correspondenceLanguage: customer.correspondenceLanguage?.toLowerCase(),
      phone: customer.privateContact.phone,
      personMobilePhone: customer.privateContact.mobile,
      personEmail: personEmail,
      privateAddressCity: customer.privateContact.address?.city,
      privateAddressCountry: customer.privateContact.address?.country,
      privateAddressComplement: customer.privateContact.address?.addressLine1,
      privateAddressLine2: customer.privateContact.address?.addressLine2,
      privateAddressPostBox: customer.privateContact.address?.postBox,
      privateAddressPostalCode: customer.privateContact.address?.postalCode,
      privateAddressStreet: customer.privateContact.address?.street,
      privateAddressStreetNumber: customer.privateContact.address?.houseNumber,
      privateAddressState: customer.privateContact.address?.province,
      salutation: this.customerFieldModelMapper.mapToRetailCrmSalutation(customer.salutation),
      title: this.customerFieldModelMapper.mapToRetailCrmTitle(customer.title),
      nationalId1: customer.nationalId1,
      nationalId1DocumentType: this.customerFieldModelMapper.mapToNationalIdDocumentType(
        customer.nationalId1DocumentType,
      ),
      taxNumber: customer.taxNumber,
      taxNumberCountry: customer.taxNumberCountry,
    };
  }
}
